import { useCallback, useRef, useState } from "react"
import { Alert, Button, Empty, Modal, Popconfirm, Skeleton, Space, Tag, Input } from "antd"
import { Check, Copy, Pencil, Plus, Star, Trash2, X } from "lucide-react"
import { useNavigate, useParams } from "react-router-dom"
import { BREAKPOINTS, type DashboardDetail, type TileItem } from "@csbi/contracts"
import type { MetricDefinition } from "@csbi/metric-catalog"
import type { ThemeMode } from "@csbi/viz-core"
import { useTheme } from "../../app/providers/theme-provider.js"
import { DashboardCanvas } from "../../features/dashboards/components/dashboard-canvas.js"
import { MetricPicker } from "../../features/dashboards/components/metric-picker.js"
import { TileSettings, applyPatch, type SettingsPatch } from "../../features/dashboards/components/tile-settings.js"
import { toGridLayouts, type Layouts } from "../../features/dashboards/grid.js"
import { placeAfter, tileForMetric } from "../../features/dashboards/new-tile.js"
import {
  useCreateDashboard,
  useDashboard,
  useDeleteDashboard,
  useSaveDashboardEdits,
  useUpdateDashboard,
} from "../../features/dashboards/use-dashboards.js"
import { PageHeader } from "../../shared/components/page-header.js"

type Draft = {
  name: string
  description: string
  tiles: TileItem[]
  layouts: Layouts
  dirty: boolean
}

export function DashboardDetailPage() {
  const { slug = "" } = useParams()
  const { mode } = useTheme()
  const { data, isLoading, error } = useDashboard(slug)

  if (isLoading) {
    return (
      <>
        <PageHeader title="Dashboard" />
        <Skeleton active paragraph={{ rows: 8 }} />
      </>
    )
  }

  if (error || !data) {
    return <Empty description={`No dashboard named ${slug}`} />
  }

  // Key on the id: switching dashboards throws the whole edit state away,
  // or half-editing A and moving to B would carry A's draft into B
  return <DashboardView key={data.id} dashboard={data} mode={mode} />
}

function DashboardView({ dashboard, mode }: { dashboard: DashboardDetail; mode: ThemeMode }) {
  const navigate = useNavigate()
  const [draft, setDraft] = useState<Draft | null>(null)
  const [picking, setPicking] = useState<{ after: string | null } | null>(null)
  const [configuring, setConfiguring] = useState<string | null>(null)

  const save = useSaveDashboardEdits(dashboard.slug)
  const update = useUpdateDashboard(dashboard.slug)

  const touched = useRef(false)

  const tiles = draft?.tiles ?? dashboard.tiles
  const editing = draft !== null

  const onLayoutsChange = useCallback((next: Layouts) => {
    setDraft((d) => (d ? { ...d, layouts: next, dirty: d.dirty || touched.current } : d))
  }, [])

  const enterEdit = () => {
    touched.current = false
    setDraft({
      name: dashboard.name,
      description: dashboard.description ?? "",
      tiles: [...dashboard.tiles],
      layouts: toGridLayouts(dashboard.tiles),
      dirty: false,
    })
  }

  const leaveEdit = () => {
    touched.current = false
    setDraft(null)
    setPicking(null)
    setConfiguring(null)
    save.reset()
  }

  const removeTile = (tileId: string) => {
    touched.current = true
    setDraft((d) => {
      if (!d) return d
      const layouts = {} as Layouts
      for (const bp of BREAKPOINTS) layouts[bp] = (d.layouts[bp] ?? []).filter((i) => i.i !== tileId)
      return { ...d, tiles: d.tiles.filter((t) => t.id !== tileId), layouts, dirty: true }
    })
  }

  const addTiles = (metrics: MetricDefinition[]) => {
    const after = picking?.after ?? null
    setPicking(null)
    if (metrics.length === 0) return
    touched.current = true

    setDraft((d) => {
      if (!d) return d
      const base = d.tiles.length
      const created = metrics.map((m, i) => tileForMetric(dashboard.id, m, base + i))
      const at = after ? d.tiles.findIndex((t) => t.id === after) : -1
      const tiles = [...d.tiles]
      tiles.splice(at >= 0 ? at + 1 : tiles.length, 0, ...created)

      return { ...d, tiles, layouts: placeAfter(d.layouts, after, created, BREAKPOINTS), dirty: true }
    })
  }

  const applySettings = (tileId: string, patch: SettingsPatch) => {
    touched.current = true
    setConfiguring(null)
    setDraft((d) =>
      d
        ? {
            ...d,
            dirty: true,
            tiles: d.tiles.map((t) =>
              // Assembling config is `applyPatch`'s job — writing the field names again here means that when the
              // drawer gains an option, somebody will forget to connect it here (and it will change nothing)
              t.id === tileId
                ? { ...t, title: patch.title, widgetType: patch.widgetType, config: applyPatch(t.config, patch) }
                : t,
            ),
          }
        : d,
    )
  }

  const commit = () => {
    if (!draft || draft.name.trim() === "") return
    save.mutate(
      {
        name: draft.name.trim(),
        description: draft.description.trim() || null,
        tiles: draft.tiles,
        layouts: draft.layouts,
      },
      { onSuccess: leaveEdit },
    )
  }

  const onCanvas = editing
    ? {
        editable: true,
        layouts: draft.layouts,
        onLayoutsChange,
        onInteractionEnd: () => {
          touched.current = true
        },
        onRemoveTile: removeTile,
        onAddAfter: (id: string) => setPicking({ after: id }),
        onConfigureTile: setConfiguring,
      }
    : {}

  const configured = draft?.tiles.find((t) => t.id === configuring) ?? null
  const alreadyOn = new Set(tiles.flatMap((t) => t.config.metrics))

  return (
    <>
      <PageHeader
        title={
          editing ? (
            <Input
              value={draft.name}
              onChange={(e) => setDraft((d) => (d ? { ...d, name: e.target.value, dirty: true } : d))}
              maxLength={80}
              placeholder="Dashboard name"
              status={draft.name.trim() === "" ? "error" : undefined}
              style={{ fontSize: 20, fontWeight: 650, padding: "2px 8px" }}
            />
          ) : (
            dashboard.name
          )
        }
        {...(editing
          ? {
              description: (
                <Input
                  value={draft.description}
                  onChange={(e) => setDraft((d) => (d ? { ...d, description: e.target.value, dirty: true } : d))}
                  maxLength={500}
                  placeholder="Description (optional) — shows on the dashboard list"
                  style={{ marginTop: 4, fontSize: 13 }}
                />
              ),
            }
          : dashboard.description
            ? { description: dashboard.description }
            : {})}
        extra={
          <Space size={8}>
            {dashboard.isHome && !editing && <Tag color="blue">Home</Tag>}
            <Tag>
              {tiles.length} {tiles.length === 1 ? "tile" : "tiles"}
            </Tag>
            {editing ? (
              <>
                <Button size="small" icon={<Plus size={14} />} onClick={() => setPicking({ after: null })}>
                  Add tiles
                </Button>
                <Button size="small" icon={<X size={14} />} onClick={leaveEdit} disabled={save.isPending}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  type="primary"
                  icon={<Check size={14} />}
                  onClick={commit}
                  disabled={!draft.dirty || draft.name.trim() === ""}
                  loading={save.isPending}
                >
                  Save
                </Button>
              </>
            ) : (
              <>
                <Button size="small" icon={<Pencil size={14} />} onClick={enterEdit}>
                  Edit
                </Button>
                {!dashboard.isHome && (
                  <Button
                    size="small"
                    icon={<Star size={14} />}
                    loading={update.isPending}
                    onClick={() => update.mutate({ isHome: true })}
                  >
                    Set as home
                  </Button>
                )}
                <DuplicateDashboard dashboard={dashboard} onCreated={(s) => navigate(`/dashboards/${s}`)} />
                <DeleteDashboard dashboard={dashboard} onDeleted={() => navigate("/dashboards")} />
              </>
            )}
          </Space>
        }
      />

      {save.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Changes not saved"
          description={save.error instanceof Error ? save.error.message : "The server rejected the changes."}
        />
      )}

      {tiles.length === 0 && !editing ? (
        <Empty description="This dashboard has no tiles yet">
          <Button icon={<Pencil size={14} />} onClick={enterEdit}>
            Edit and add tiles
          </Button>
        </Empty>
      ) : (
        <DashboardCanvas dashboard={dashboard} tiles={tiles} mode={mode} {...onCanvas} />
      )}

      <MetricPicker
        open={picking !== null}
        alreadyOn={alreadyOn}
        onCancel={() => setPicking(null)}
        onConfirm={addTiles}
      />
      <TileSettings
        tile={configured}
        onCancel={() => setConfiguring(null)}
        onSave={(patch) => configuring && applySettings(configuring, patch)}
      />
    </>
  )
}

function DuplicateDashboard({
  dashboard,
  onCreated,
}: {
  dashboard: DashboardDetail
  onCreated: (slug: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState(`${dashboard.name} (Copy)`)
  const create = useCreateDashboard()

  const submit = () =>
    create.mutate(
      { name: name.trim(), description: dashboard.description, copyFrom: dashboard.slug },
      {
        onSuccess: ({ slug }) => {
          setOpen(false)
          onCreated(slug)
        },
      },
    )

  return (
    <>
      <Button size="small" icon={<Copy size={14} />} onClick={() => setOpen(true)}>
        Duplicate
      </Button>
      <Modal
        open={open}
        title="Duplicate dashboard"
        okText="Create"
        okButtonProps={{ disabled: name.trim().length === 0, loading: create.isPending }}
        onOk={submit}
        onCancel={() => setOpen(false)}
      >
        <Input value={name} onChange={(e) => setName(e.target.value)} onPressEnter={submit} maxLength={80} autoFocus />
        {create.isError && (
          <Alert
            type="error"
            showIcon
            style={{ marginTop: 12 }}
            message={create.error instanceof Error ? create.error.message : "Could not create the dashboard."}
          />
        )}
      </Modal>
    </>
  )
}

function DeleteDashboard({ dashboard, onDeleted }: { dashboard: DashboardDetail; onDeleted: () => void }) {
  const remove = useDeleteDashboard()

  return (
    <Popconfirm
      title="Delete this dashboard?"
      description={`"${dashboard.name}" and its ${dashboard.tileCount} tiles will be gone. This cannot be undone.`}
      okText="Delete"
      okButtonProps={{ danger: true, loading: remove.isPending }}
      onConfirm={() => remove.mutate(dashboard.slug, { onSuccess: onDeleted })}
    >
      <Button size="small" danger icon={<Trash2 size={14} />}>
        Delete
      </Button>
    </Popconfirm>
  )
}
